import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { RootState } from "../../app/store";
import { CalendarItem } from "./models/CalendarItem";
import { CalendarFeed, fetchCalendar } from "./slice-extensions/calendar-slice-extension";

interface CalendarState {
  items: CalendarItem[];
  from?: string; // YYYY-MM-DD of the loaded range
  to?: string;
  /** Narrow the feed to one colleague's sessions; null = all instructors. */
  instructorFilter: string | null;
  status: LoadingStatus;
  lastError?: ApiErrorResponse;
}

const initialState: CalendarState = {
  items: [],
  from: undefined,
  to: undefined,
  instructorFilter: null,
  status: LoadingStatus.Idle,
  lastError: undefined,
};

const calendarSlice = createSlice({
  name: "calendar",
  initialState,
  reducers: {
    clearCalendarError: (state) => {
      state.lastError = undefined;
    },
    setInstructorFilter: (state, action: PayloadAction<string | null>) => {
      state.instructorFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCalendar.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(fetchCalendar.fulfilled, (state, action: PayloadAction<CalendarFeed>) => {
        state.status = LoadingStatus.Idle;
        state.items = action.payload.items;
        state.from = action.payload.from;
        state.to = action.payload.to;
      })
      .addCase(fetchCalendar.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.lastError = action.payload;
      });
  },
});

export const { clearCalendarError, setInstructorFilter } = calendarSlice.actions;

export const selectCalendarItems = (state: RootState) => state.calendar.items;
export const selectCalendarInstructorFilter = (state: RootState) =>
  state.calendar.instructorFilter;
export const selectCalendarStatus = (state: RootState) => state.calendar.status;
export const selectCalendarError = (state: RootState) => state.calendar.lastError;

export default calendarSlice.reducer;
